import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";
import { EnvironmentOutlined, DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  m,
  useScroll,
  useTransform,
} from "framer-motion";
import { ssr } from "@/lib/ssrState";
import OptImg from "@/components/OptImg";

// Reusable variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.45, ease: [0.25, 1, 0.5, 1] },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { delay: i * 0.05, duration: 0.45, ease: "easeOut" },
  }),
};

const scalePop = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.35, ease: [0.25, 1, 0.5, 1] },
  },
};

const experiences = [
  {
    icon: <EnvironmentOutlined className="text-[#c9785a]" />,
    title: "Temperature Controlled",
    desc: "Maintain the ideal storage temperature for your goods with advanced control features.",
  },
  {
    icon: <EnvironmentOutlined className="text-[#c9785a]" />,
    title: "Flexible Sizes",
    desc: "Choose from a variety of container sizes to match your exact storage requirements.",
  },
  // Add more features...
];

const NAV_H = 72; // adjust to your real navbar height (px)

const HeroSection = ({ primaryColor = "black" }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "id" ? "id" : "en";

  const { scrollYProgress } = useScroll(); // parallax
  const yTitle = useTransform(scrollYProgress, [0, 0.4], [0, -40]); // move up slightly

  const scrollToNext = () => {
    const el = document.getElementById("features");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <m.section
      id="hero"
      className="relative px-6 sm:px-12 flex items-center justify-center pb-24 overflow-visible"
      style={{ minHeight: `calc(100svh - ${NAV_H}px)` }}
      initial={ssr.prerender ? false : "hidden"}
      animate="visible"
      variants={containerStagger}
    >
      <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row justify-between items-start gap-8 max-h-[90vh]">
        {/* Left: Big title (parallax) */}
        <m.div
          className="sm:flex flex-col sm:flex-row sm:items-start"
          variants={fadeIn}
        >
          <div className="flex flex-row items-start">
            <m.h1
              style={{ y: yTitle, color: primaryColor }}
              className="text-[60px] sm:text-[150px] leading-none font-bold font-sans tracking-tight select-none will-change-transform"
            >
              {t("hero.titleLine1")} <br /> {t("hero.titleLine2")}
            </m.h1>
          </div>
        </m.div>

        {/* Right: Subtitle + CTAs */}
        <m.div
          className="mt-0 sm:mt-8 sm:ml-16 flex flex-col items-start w-full sm:w-auto"
          variants={fadeInUp}
          custom={0.2}
        >
          <span
            className="text-[30px] sm:text-[52px] font-bold leading-[1.1] block mb-7"
            style={{ color: primaryColor }}
          >
            {t("hero.subtitleLine1")} <br /> {t("hero.subtitleLine2")}
          </span>

          <m.div
            className="flex flex-col space-y-4 w-full max-w-[500px] sm:flex-row sm:space-y-0 sm:space-x-6 sm:w-auto sm:mt-4"
            variants={containerStagger}
          >
            <m.div variants={scalePop}>
              <Link
                to={`/${lang}/service/buy`}
                className="w-full sm:w-auto text-white rounded-full px-6 sm:px-8 py-3 text-base sm:text-xl font-semibold hover:opacity-80 transition flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                {t("hero.buy")}
              </Link>
            </m.div>
            <m.div variants={scalePop}>
              <Link
                to={`/${lang}/service/rent`}
                className="w-full sm:w-auto text-white rounded-full px-6 sm:px-8 py-3 text-base sm:text-xl font-semibold hover:opacity-80 transition flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                {t("hero.rent")}
              </Link>
            </m.div>
          </m.div>
        </m.div>
      </div>

      {/* Scroll button */}
      <m.button
        type="button"
        onClick={scrollToNext}
        aria-label="Scroll to content"
        className="group absolute bottom-6 left-1/2 -translate-x-1/2 h-14 w-14 rounded-full bg-white/90 border backdrop-blur shadow-lg flex items-center justify-center z-50 focus:outline-none focus:ring-2"
        style={{ borderColor: primaryColor }}
        initial={{ opacity: 1, y: 12 }}
        animate={{ opacity: 1, y: [0, -6, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        whileHover={{
          y: 0,
          scale: 1.04,
          transition: { type: "spring", stiffness: 220, damping: 14 },
        }}
      >
        <DownOutlined
          className="text-xl group-hover:translate-y-0.5 transition-transform"
          style={{ color: primaryColor }}
        />
      </m.button>
    </m.section>
  );
};

const FeatureSection = ({ primaryColor = "black" }) => {
  const { t } = useTranslation();
  const features = t("featureSection.features", { returnObjects: true });

  return (
    <m.section
      id="features"
      className="min-h-screen flex flex-col justify-center bg-white px-6 md:px-12"
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2, once: true }}
      variants={containerStagger}
    >
      <m.div className="w-full flex justify-center" variants={fadeIn}>
        <div className="max-w-5xl w-full">
          <div
            className="text-4xl md:text-[90px] font-bold leading-tight md:leading-[0.98] text-left mb-12 md:mb-16"
            style={{ fontFamily: "inherit", color: primaryColor }}
          >
            {t("featureSection.heading")}
            <br className="hidden md:block" />
          </div>
        </div>
      </m.div>

      <div className="w-full flex justify-center">
        <m.div
          className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-y-12 md:gap-x-16"
          variants={containerStagger}
        >
          {features.map((feature, idx) => (
            <m.div key={idx} variants={fadeInUp} custom={idx * 0.05}>
              <m.hr
                className="mb-4"
                style={{ borderColor: primaryColor }}
                initial={{ scaleX: 0, originX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true }}
              />
              <div
                className="text-xl md:text-2xl font-bold mb-3"
                style={{ color: primaryColor }}
              >
                {feature.title}
              </div>
              <div className="text-gray-500 text-base md:text-lg font-normal text-left">
                {feature.desc}
              </div>
            </m.div>
          ))}
        </m.div>
      </div>
    </m.section>
  );
};

const ServicesSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "id" ? "id" : "en";

  const services = useMemo(
    () => [
      {
        key: "buy",
        titleKey: "servicesSection.buy.title",
        descKey: "servicesSection.buy.desc",
        img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_buy_xmzlge.jpg",
        link: `/${lang}/service/buy`,
      },
      {
        key: "rent",
        titleKey: "servicesSection.rent.title",
        descKey: "servicesSection.rent.desc",
        img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237549/Bagian_rent_qvhskt.jpg",
        link: `/${lang}/service/rent`,
      },
    ],
    [lang]
  );

  return (
    <m.section
      className="w-full min-h-[70vh] grid grid-cols-1 md:grid-cols-3 overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerStagger}
    >
      {/* Left intro */}
      <m.div
        className="bg-[#161722] flex flex-col justify-between p-8 md:p-12"
        variants={fadeInUp}
      >
        <div>
          <div className="text-3xl md:text-[48px] font-bold text-white mb-4">
            {t("servicesSection.heading")}
          </div>
          <div className="text-white text-base md:text-xl font-semibold mb-12 text-left">
            {t("servicesSection.subheading")}
          </div>
        </div>

        <div className="hidden md:flex flex-col gap-6">
          <div className="text-gray-400">Click to explore:</div>
          {services.map((svc) => (
            <Link
              key={svc.key}
              to={svc.link}
              className="text-white font-semibold text-lg py-2 px-6 rounded-full border border-white hover:bg-white hover:text-black transition-colors duration-300 mb-2 text-center"
            >
              {t(svc.titleKey)}
            </Link>
          ))}
        </div>
      </m.div>

      {/* Cards */}
      {services.map((svc) => (
        <m.div
          key={svc.key}
          variants={item}
          className="relative min-h-[33vh] md:min-h-0 overflow-hidden"
        >
          <Link
            to={svc.link}
            className="group relative flex flex-col justify-end w-full h-full cursor-pointer overflow-hidden block"
          >
            <OptImg
              src={svc.img}
              alt={t(svc.titleKey)}
              className="absolute inset-0 object-cover w-full h-full rounded-none transition-transform duration-700 ease-out group-hover:scale-105 will-change-transform transform-gpu"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/60 group-hover:bg-black/70 transition-colors duration-300" />
            <div className="relative z-10 p-8 md:pb-14 flex flex-col transform transition-transform duration-300 ease-out group-hover:-translate-y-1">
              <div className="text-white text-2xl md:text-[36px] font-bold drop-shadow-lg mb-2">
                {t(svc.titleKey)}
              </div>
              <div className="text-white text-base md:text-lg font-medium drop-shadow-lg text-left">
                {t(svc.descKey)}
              </div>
            </div>
          </Link>
        </m.div>
      ))}
    </m.section>
  );
};

const SOLUTIONS = [
  {
    labelKey: "solutions.0.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-11_n6uox1.svg",
  },
  {
    labelKey: "solutions.1.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-13_esfbc7.svg",
  },
  {
    labelKey: "solutions.2.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119980/asset-16_uglgs9.svg",
  },
  {
    labelKey: "solutions.3.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-10_zko5oo.svg",
  },
  {
    labelKey: "solutions.4.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-14_eran7x.svg",
  },
  {
    labelKey: "solutions.5.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119980/asset-17_mc0mmk.svg",
  },
  {
    labelKey: "solutions.6.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-12_au3gd2.svg",
  },
  {
    labelKey: "solutions.7.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119978/asset-15_v8ta47.svg",
  },
  {
    labelKey: "solutions.8.label",
    icon: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754119980/asset-19_sujv8h.svg",
  },
];

const SolutionNeedsSection = () => {
  const { t } = useTranslation();
  return (
    <m.section
      className="w-full bg-black text-white py-12 px-4 md:py-16 md:px-0"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={containerStagger}
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-10">
        <m.div
          className="hidden md:block w-[220px] h-[140px] lg:w-[360px] lg:h-[480px] rounded-2xl overflow-hidden flex-shrink-0 shadow-xl"
          variants={fadeInUp}
        >
          <OptImg
            src="https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_various_vu6kcy.jpg"
            alt="Cold Storage Containers"
            className="object-cover w-full h-full grayscale"
          />
        </m.div>

        <m.div
          className="flex-1 flex flex-col gap-6 items-start"
          variants={containerStagger}
        >
          <m.div variants={fadeInUp}>
            <h2 className="text-2xl md:text-4xl font-bold leading-tight mb-2">
              {t("solutionNeeds.heading")}
            </h2>
            <div className="text-white/90 text-base md:text-lg font-medium mb-6 max-w-lg text-left">
              {t("solutionNeeds.desc")}
            </div>
          </m.div>

          <m.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 w-full"
            variants={containerStagger}
          >
            {SOLUTIONS.map(({ icon, labelKey }, idx) => (
              <m.div
                key={idx}
                className="flex items-center gap-3 bg-black rounded-full px-5 md:px-7 py-2 md:py-3 border border-white/20 hover:border-white/40 transition-colors duration-200"
                variants={scalePop}
                whileHover={{ y: -2 }}
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-black border border-white/20 shadow-inner overflow-hidden">
                  <OptImg
                    src={icon}
                    alt={t(labelKey)}
                    className="block w-7 h-7 object-contain"
                  />
                </span>
                <span className="font-medium text-base md:text-lg text-white whitespace-nowrap truncate">
                  {t(labelKey)}
                </span>
              </m.div>
            ))}
          </m.div>
        </m.div>
      </div>
    </m.section>
  );
};

const PROJECTS = [
  {
    titleKey: "projects.0.title",
    descKey: "projects.0.desc",
    img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1754812866/WhatsApp_Image_2025-08-07_at_15.55.22_1_eh4x6s.jpg",
  },
  {
    titleKey: "projects.1.title",
    descKey: "projects.1.desc",
    img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1754812890/WhatsApp_Image_2025-08-07_at_15.55.22_ikyiab.jpg",
  },
  {
    titleKey: "projects.2.title",
    descKey: "projects.2.desc",
    img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1754812894/WhatsApp_Image_2025-08-07_at_15.55.19_oqxcrg.jpg",
  },
];

const containerStagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.02 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
  },
};

const OurProjectSection = ({ projects = PROJECTS }) => {
  const { t } = useTranslation();
  return (
    <m.section
      className="w-full bg-gradient-to-b from-white to-[#f8fafc] py-20 px-4 md:px-16 border-t border-[#101616]/10"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerStagger}
    >
      <div className="max-w-7xl mx-auto">
        <m.h2
          className="text-[36px] md:text-[56px] font-bold text-black mb-16 text-center md:text-left"
          variants={item}
        >
          {t("projectsSection.heading")}
        </m.h2>

        <m.div
          className="grid gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          variants={containerStagger}
        >
          {projects.map((p, idx) => (
            <m.div
              key={idx}
              className="relative group rounded-3xl overflow-hidden border border-black/10 bg-white transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-2xl will-change-transform transform-gpu"
              variants={item}
              style={{ boxShadow: "0 2px 20px 0 rgba(30,30,30,0.08)" }}
            >
              {/* Aspect ratio container */}
              <div className="w-full aspect-[16/9] overflow-hidden relative">
                <OptImg
                  src={p.img}
                  alt={t(p.titleKey)}
                  className="object-cover w-full h-full select-none pointer-events-none transition-transform duration-500 ease-out group-hover:scale-105 will-change-transform transform-gpu"
                  decoding="async"
                  loading="lazy"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/5 to-transparent pointer-events-none" />
              </div>

              <div className="relative z-10 p-6 flex flex-col gap-2">
                <div className="font-bold text-xl text-black">
                  {t(p.titleKey)}
                </div>
                <div className="text-base text-[#222] opacity-80 text-left">
                  {t(p.descKey)}
                </div>
              </div>
            </m.div>
          ))}
        </m.div>
      </div>
    </m.section>
  );
};

const LandingPage = () => {
  return (
    <div className="bg-white">
      <div className="mb-30">
        <HeroSection />
      </div>
      <div className="mb-24">
        <FeatureSection />
      </div>
      <ServicesSection />
      <SolutionNeedsSection />
      <OurProjectSection />

      {/* <WhatsAppFloatingButton /> */}
    </div>
  );
};

export default LandingPage;
